import { useState, type ReactNode } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useSalesforceOrg } from '@/api/hooks/use-salesforce';
import { isPlaceholderSalesforceOrgId } from '@/features/salesforce/components';
import {
  useTelephonyConnection,
  useUpdateTelephonyConnection,
} from '@/api/hooks/use-telephony';
import { ApiError } from '@/api/errors';
import { getResourceHub, type ResourceType } from '@/config/resource-hubs';
import { HubContent } from '@/shared/components/HubLayout';
import { PageSkeleton } from '@/shared/components/PageSkeleton';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { HubTabBar, ResourcePageHeader } from './components';

function orgHostname(instanceUrl?: string | null) {
  if (!instanceUrl) return undefined;
  try {
    return new URL(instanceUrl).hostname;
  } catch {
    return instanceUrl;
  }
}

type ResourceHubShellProps = {
  basePath: string;
  resourceType: ResourceType;
  title: string | ReactNode;
  subtitle?: string;
  status?: string;
  environment?: string;
  breadcrumbLabel: string;
  titleActions?: ReactNode;
};

function ResourceHubShell({
  basePath,
  resourceType,
  title,
  subtitle,
  status,
  environment,
  breadcrumbLabel,
  titleActions,
}: ResourceHubShellProps) {
  const hub = getResourceHub(resourceType);

  return (
    <HubContent width="lg">
      <ResourcePageHeader
        title={title}
        subtitle={subtitle}
        status={status}
        environment={environment}
        breadcrumbs={[hub.breadcrumbParent, { label: breadcrumbLabel }]}
        titleActions={titleActions}
      />
      <HubTabBar tabs={hub.tabs} basePath={basePath} />
      <div className="pt-1">
        <Outlet />
      </div>
    </HubContent>
  );
}

export function SalesforceOrgHubLayout() {
  const { orgId = '' } = useParams();
  const { data: org, isLoading } = useSalesforceOrg(orgId);
  const hub = getResourceHub('salesforce_org');
  const basePath = hub.basePath(orgId);

  if (isLoading) {
    return (
      <HubContent width="lg">
        <PageSkeleton variant="hub" />
      </HubContent>
    );
  }

  const orgIdLabel =
    org?.salesforce_org_id && !isPlaceholderSalesforceOrgId(org.salesforce_org_id)
      ? org.salesforce_org_id
      : null;
  const subtitle = [orgIdLabel, orgHostname(org?.instance_url)].filter(Boolean).join(' · ');

  return (
    <ResourceHubShell
      resourceType="salesforce_org"
      basePath={basePath}
      title={org?.display_name ?? 'Salesforce Organization'}
      subtitle={subtitle || undefined}
      status={org?.status === 'active' ? 'active' : 'error'}
      environment={org?.environment}
      breadcrumbLabel={org?.display_name ?? `${orgId.slice(0, 8)}…`}
    />
  );
}

export function TelephonyConnectionHubLayout() {
  const { connectionId = '' } = useParams();
  const { can } = usePermissions();
  const { data: connection, isLoading } = useTelephonyConnection(connectionId);
  const updateConnection = useUpdateTelephonyConnection(connectionId);
  const hub = getResourceHub('telephony_connection');
  const basePath = hub.basePath(connectionId);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  if (isLoading) {
    return (
      <HubContent width="lg">
        <PageSkeleton variant="hub" />
      </HubContent>
    );
  }

  const providerLabel =
    connection?.provider_display_name ?? connection?.provider_code ?? 'Provider';
  const canEdit = can('telephony:edit');
  const displayName = connection?.name ?? 'Telephony Connection';

  async function handleSaveName() {
    const name = nameDraft.trim();
    if (!name) {
      toast.error('Connection name is required');
      return;
    }
    try {
      await updateConnection.mutateAsync({ name });
      toast.success('Connection name updated');
      setEditing(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Failed to update name');
    }
  }

  const titleActions =
    canEdit && !editing ? (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={() => {
          setNameDraft(connection?.name ?? '');
          setEditing(true);
        }}
        aria-label="Edit connection name"
      >
        <Pencil className="h-4 w-4" />
      </Button>
    ) : null;

  const title = editing ? (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        value={nameDraft}
        onChange={(e) => setNameDraft(e.target.value)}
        className="h-9 max-w-md text-xl font-semibold md:text-2xl"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSaveName();
          }
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <Button
        size="sm"
        onClick={() => void handleSaveName()}
        disabled={updateConnection.isPending}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setEditing(false)}
        disabled={updateConnection.isPending}
      >
        Cancel
      </Button>
    </div>
  ) : (
    displayName
  );

  return (
    <ResourceHubShell
      resourceType="telephony_connection"
      basePath={basePath}
      title={title}
      titleActions={titleActions}
      subtitle={providerLabel}
      status={connection?.status ?? 'inactive'}
      breadcrumbLabel={connection?.name ?? `${connectionId.slice(0, 8)}…`}
    />
  );
}
